import React, { useContext, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import appConfig from 'app-config';
import classNames from 'classnames';
import _ from 'lodash';
import { MLTooltipComponent } from 'ml-components-platform-react';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';

import metadata from './MLVehicleOperatorTableComponent.metadata.json5';
import messages from './MLVehicleOperatorTableComponent.messages';
import styles from './MLVehicleOperatorTableComponent.module.scss';

import { IntlContext } from '@jutro/legacy/locale';

const MLVehicleOperatorTableComponent = (props) => {
    const {
        vehicleOperators,
        onEdit,
        onRemove,
        driverType,
        getDriverTypeDisplayName,
        submissionVM,
        viewModelService
    } = props;

    const translator = useTranslator();
    const intl = useContext(IntlContext);
    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const isQnB = appConfig.portalCode === 'QB';
    const genderTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('GenderType');
    const relationTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('InsuredRelationType_ML');
    const statusTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('DriverStatusType_ML');
    const maritalStatusTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('MaritalStatus');
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const isPersonalUmbrella = submissionVM.value.isPersonalUmbrella();
    const isUMB_NP = isModernProduct && isPersonalUmbrella;

    const [filteredVehicleOperators, setFilteredVehicleOperators] = useState([]);
    const [operatorHelpText, setOperatorHelpText] = useState({
        operatorHelpTextTitle: '',
        operatorHelpTextMessage: '' 
    });
    const [btnHelp, setBbtnHelp] = useState({
        btnHelpMessageTitle: '',
        btnHelpMessage: '' 
    });
    const [pluralDriverTypeDisplayName, setPluralDriverTypeDisplayName]= useState('');

    const {
        getFullName
    } = MLDriverVehicleUtil(viewModelService);

    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);

    const checkObjectValidity = () => {
        if (vehicleOperators && vehicleOperators.children) {
            vehicleOperators.children.forEach((vehicleOperator) => {
                vehicleOperator.contact.isGenderRequired.value = true;
                vehicleOperator.isRelationshipRequired.value = true;
                vehicleOperator.isDriverStatusForAddingRequired.value = true;
                vehicleOperator.isDriverTypeRequired.value = true;

                if (vehicleOperator.driverType.value &&
                    (vehicleOperator.driverType.value.code === 'rated')) {
                    vehicleOperator.contact.isMaritalStatusRequired.value = true;
                } else {
                    vehicleOperator.contact.isMaritalStatusRequired.value = false;
                }

                vehicleOperator.value.error = !(vehicleOperator.aspects.valid && vehicleOperator.aspects.subtreeValid);
            });

            const spouseOperators = vehicleOperators.children.filter((vehicleOperator) => vehicleOperator.relationshipToInsured.value &&
                vehicleOperator.relationshipToInsured.value.code === 'spouse');

            if (spouseOperators.length > 1) {
                vehicleOperators.children.forEach((vehicleOperator) => {
                    if (vehicleOperator.relationshipToInsured.value && vehicleOperator.relationshipToInsured.value.code === 'spouse') {
                        vehicleOperator.value.error = true;
                    }
                });
            }

            const pNIVehicleOperator = vehicleOperators.children.filter((vehicleOperator) => {
                return vehicleOperator.isPrimaryNamedInsured.value &&
                    vehicleOperator.relationshipToInsured.value &&
                    vehicleOperator.relationshipToInsured.value.code === 'self';
            });
            if (pNIVehicleOperator.length !== 1) {
                vehicleOperators.children.forEach((vehicleOperator) => {
                    if (vehicleOperator.isPrimaryNamedInsured.value || vehicleOperator.relationshipToInsured.value.code === 'self') {
                        vehicleOperator.value.error = true;
                    }
                });
            }
        }
    };

    const filterVehicleOperatorsByDriverType = (vehicleOperatorsValue) => {
        return vehicleOperatorsValue.filter((vehOperator) => {
            if (isUMB_NP && driverType === 'household') {
            return ['nondriver', 'permitted', 'outofhousehold'].includes(vehOperator.driverType);
            }
            if (vehOperator.driverType === driverType) {
                return true;
            } 
            if (driverType === 'household') {
                return !vehOperator.driverType;
            }
            return false;
        });
    };

    const setupOperatorHelpTextByDriverType = () => {
        if (driverType === 'excluded') {
            setOperatorHelpText({
                operatorHelpTextTitle: translator(messages.howToChangeFromExcluded),
                operatorHelpTextMessage: translator(messages.excludedDriverHelpTextMsg)
            });
        } else if (driverType === 'household') {
            setOperatorHelpText({
                operatorHelpTextTitle: translator(messages.howToChangeFromHouseholdMember),
                operatorHelpTextMessage: translator(messages.householdMemberHelpTextMsg)
            });
        }
    };

    const setupActionBtnHelpLabel = () => {
        if (driverType === 'excluded' && isQnB) {
            setBbtnHelp({
                btnHelpMessageTitle: translator(messages.whatDoesThisMean),
                btnHelpMessage: translator(messages.driverWillBeExcludedFromLiabilityCov)
            });
        }
    };

    const checkForErrors = (vehicleOperatorsValue) => {
        vehicleOperatorsValue.forEach((operator) => {
            if (!_.isUndefined(operator.contact.gender) && !_.isUndefined(operator.contact.maritalStatus) && !_.isUndefined(operator.relationshipToInsured)) {
                if (_.isUndefined(operator.driverStatusForAdding)) {
                    operator.driverStatusForAdding = 'insure_driver';
                }
                operator.error = false;
            }
        });
    };  

    const setScopeVariables = (vehicleOperatorsValue) => {
        setPluralDriverTypeDisplayName(getDriverTypeDisplayName(driverType, false));
        const filteredOperators = filterVehicleOperatorsByDriverType(vehicleOperatorsValue);
        filteredOperators.forEach((item) => {
            item.contact.fullName = getFullName(item.contact);
        });
        setFilteredVehicleOperators(filteredOperators);
        setupOperatorHelpTextByDriverType();
        setupActionBtnHelpLabel();
    };

    const initVehicleOperators = () => {
        checkObjectValidity(vehicleOperators);
        setScopeVariables(vehicleOperators.value);
        checkForErrors(vehicleOperators.value);
    };

    useEffect(() => {
        initVehicleOperators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* useEffect(() => {
        setScopeVariables(newValue);
    }, [vehicleOperators.value]); */

    const isRatedDriver = () => {
        return driverType === 'rated';
    };

    const editVehicleOperator = useCallback((callbackProps) => {
        if (onEdit) {
            onEdit(filteredVehicleOperators[callbackProps.index]);
        }
    }, [filteredVehicleOperators, onEdit]);

    const removeVehicleOperator = useCallback((callbackProps) => {
        if (onRemove) {
            onRemove(filteredVehicleOperators[callbackProps.index]);
        }
    }, [filteredVehicleOperators, onRemove]);

    const getOperatorHeaderContent = () => {
        return (
            <>
                <span>{pluralDriverTypeDisplayName}</span>
                {/* Tooltip not shown in PE for v9. */}
                {/* {
                    operatorHelpText.operatorHelpTextTitle && isGPA && (
                        <MLTooltipComponent
                            tooltipHeader={operatorHelpText.operatorHelpTextTitle}
                            tooltipContent={operatorHelpText.operatorHelpTextMessage}
                            noWrapper
                        /> 
                    )
                }                 */}
            </>
        )
    };

    const getTableHeaderMessage = () => {
        if (filteredVehicleOperators.length === 0) {
            if(driverType === 'household' && !isUMB_NP) {
                return messages.tellWhoAreNotListed
            }
        
            if((driverType === 'household' || driverType === 'nondriver' || driverType === 'permitted' || driverType === 'outofhousehold') && isUMB_NP) {
                return messages.tellWhoAreNotListedNP
            }
            return null;
        } 
        return isUMB_NP && (driverType === 'household' || driverType === 'nondriver' || driverType === 'permitted' || driverType === 'outofhousehold') ? messages.tellWhoAreNotListedNP : driverType === 'household' ? messages.peopleDoNotDriveOrHaveOtherInsurance : messages.belowDriversExcluded;
    }

    const generateVehicleOperatorsOverrides = () => {
        if (!_.isEmpty(filteredVehicleOperators)) {
            const getTypeKeyDescription = (typeKeyName) => {
                return translator({
                    id: typeKeyName,
                    defaultMessage: typeKeyName
                });
            };

            const overrides = filteredVehicleOperators.map((item, index) => {
                return {
                    [`nameCell${index}`]: {
                        content: item.contact.fullName
                    },
                    [`nameCellStatus${index}`]: {
                        visible: _.isString(item.error)
                    },
                    [`dobCell${index}`]: {
                        content: item.contact.dateOfBirthForNonDriver ? getFormattedDateString(item.contact.dateOfBirthForNonDriver) : ''
                    },
                    [`genderCell${index}`]: {
                        content: item.contact.gender ? getTypeKeyDescription(genderTypeList.getCode(item.contact.gender).name) : ''
                    },
                    [`maritalStatusCell${index}`]: {
                        content: item.contact.maritalStatus ? getTypeKeyDescription(maritalStatusTypelist.getCode(item.contact.maritalStatus).name) : ''
                    },
                    [`relationshipToInsuredCell${index}`]: {
                        content: item.relationshipToInsured ? getTypeKeyDescription(relationTypeList.getCode(item.relationshipToInsured).name) : ''
                    },
                    [`reasonNotListedCell${index}`]: {
                        content: item.driverStatusForAdding ? getTypeKeyDescription(statusTypeList.getCode(item.driverStatusForAdding).name) : ''
                    },
                    [`editActionCell${index}`]: {
                        content: translator(messages.edit),
                        'aria-labelledby': `nameCellContent${index} dobCellContent${index} genderCellContent${index} maritalStatusCellContent${index}`
                    },
                    [`removeActionCell${index}`]: {
                        content: translator(messages.remove),
                        'aria-labelledby': `nameCellContent${index} dobCellContent${index} genderCellContent${index} maritalStatusCellContent${index}`
                    }
                };
            });
            return Object.assign({}, ...overrides);
        }
        return {};
    };
  
    const overrides = {
        headerContainer: {
            className: isRatedDriver(driverType) ? 'vertical-spacing-20' : ''
        },
        operatorTip: {
            className: isGPA ? 'section-header' : 'amp-section-header',
            content: getOperatorHeaderContent()
        },
        nonRatedContainer: {
            visible: driverType !== 'rated'
        },
        ratedContainer: {
            visible: driverType === 'rated'
        },
        headerMessage: {
            content: translator(getTableHeaderMessage())
        },
        householdMembersTooltipContainer: {
            visible: isAMP && driverType === 'household'
        },
        excludedDriversTooltipContainer: {
            visible: isAMP && driverType === 'excluded'
        },        
        vehicleOperatorTable: {
            visible: filteredVehicleOperators.length > 0 
        },
        vehicleOperatorIterable: {
            data: filteredVehicleOperators
        },
        noInformationMsg: {
            visible: filteredVehicleOperators.length === 0
        }, 

        ...generateVehicleOperatorsOverrides()
    };

    const resolvers = {
        resolveCallbackMap : {
            editVehicleOperator,
            removeVehicleOperator
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );

};

MLVehicleOperatorTableComponent.propTypes = {
    vehicleOperators: PropTypes.object,
    onEdit: PropTypes.func,
    onRemove: PropTypes.func,
    driverType: PropTypes.string,
    getDriverTypeDisplayName: PropTypes.func
};

export default MLVehicleOperatorTableComponent;
